import { Flower2, Flame, Sprout, Share2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { generateRecapImage } from "@/lib/recapImage";

export const WeeklyRecapCard = ({ recap, palette }) => {
  const [sharing, setSharing] = useState(false);
  if (!recap) return null;
  const stats = [
    { key: "perfect", icon: Flower2, value: recap.perfect_days, label: `Perfect day${recap.perfect_days === 1 ? "" : "s"}` },
    { key: "streak", icon: Flame, value: recap.streak, label: "Day streak" },
    { key: "points", icon: Sprout, value: `+${recap.points_this_week}`, label: "Garden points" },
  ];

  const share = async () => {
    const text = `My Week in Bloom 🌼 ${recap.perfect_days} perfect day${recap.perfect_days === 1 ? "" : "s"}, a ${recap.streak}-day rhythm streak and +${recap.points_this_week} garden points — growing daily with Dandelion Farm: House of Bloom 🌱 ${window.location.origin}`;
    setSharing(true);
    try {
      const blob = await generateRecapImage(recap);
      const file = new File([blob], "week-in-bloom.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text, title: "House of Bloom" });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "week-in-bloom.png";
      a.click();
      URL.revokeObjectURL(url);
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Card downloaded & caption copied — ready for Instagram 🌼");
      } catch (e) {
        toast.success("Recap card downloaded — ready for Instagram 🌼");
      }
    } catch (e) {
      toast.error("Couldn't create the share card");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      className="rounded-2xl border bg-white p-4"
      style={{ borderColor: palette.border }}
      data-testid="weekly-recap-card"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "#D97706" }}>
          This Week in Bloom
        </p>
        <button
          onClick={share}
          disabled={sharing}
          className="flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full text-white"
          style={{ backgroundColor: "#D97706", opacity: sharing ? 0.6 : 1 }}
          data-testid="recap-share-btn"
        >
          <Share2 className="w-3 h-3" /> {sharing ? "Creating…" : "Share"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {stats.map(({ key, icon: Icon, value, label }) => (
          <div key={key} className="rounded-xl py-3 px-2 text-center" style={{ backgroundColor: `${palette.primary}0D` }} data-testid={`recap-${key}`}>
            <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: palette.primary }} />
            <p className="text-lg font-bold leading-none" style={{ color: palette.primary }}>{value}</p>
            <p className="text-[10px] opacity-70 mt-1">{label}</p>
          </div>
        ))}
      </div>
      {recap.guiding_suit && (
        <div className="mt-3 rounded-xl p-3 text-center" style={{ backgroundColor: "#D9770610", border: "1px solid #D9770633" }} data-testid="recap-guiding-suit">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#B45309" }}>
            Your guiding suit this week · {recap.guiding_suit}
          </p>
          {recap.suit_whisper && (
            <p className="text-sm italic mt-1" style={{ color: "#92570A" }}>"{recap.suit_whisper}"</p>
          )}
        </div>
      )}
      {recap.cards?.length > 0 && (
        <div className="mt-3" data-testid="recap-cards">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "#B45309" }}>
            ✨ Cards that found you this week
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recap.cards.map((c, i) => (
              <span
                key={`${c.front}-${i}`}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: "#D9770614", color: "#B45309" }}
              >
                {c.suit} · {c.front}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
